import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type SupportedLanguage } from '../../i18n/config';

/**
 * Language Selector Component
 *
 * Sprint 1: Displays all 5 languages with English active and others as "Coming Soon"
 * Future: Will enable actual language switching when translations are available
 */
export const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentLanguage = i18n.language as SupportedLanguage;

  const handleLanguageSelect = (langCode: SupportedLanguage) => {
    const language = supportedLanguages[langCode];

    if (language.available) {
      // Change language (currently only en-US is available)
      i18n.changeLanguage(langCode);
      setIsOpen(false);
    } else {
      // Show coming soon message for unavailable languages
      alert(`${language.name} is coming soon! Priority: ${getPriority(langCode)}`);
    }
  };

  const getPriority = (langCode: SupportedLanguage): string => {
    switch (langCode) {
      case 'ms-MY':
        return 'Priority 1 - Post Sprint 1';
      case 'zh-CN':
      case 'zh-TW':
        return 'Priority 2';
      case 'ar-SA':
        return 'Priority 3';
      default:
        return '';
    }
  };

  return (
    <div className="relative inline-block text-left">
      {/* Language Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        aria-label={t('languages.selectLanguage')}
      >
        <span className="text-xl">{supportedLanguages[currentLanguage].flag}</span>
        <span>{supportedLanguages[currentLanguage].nativeName}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu Panel */}
          <div className="absolute right-0 z-20 w-64 mt-2 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-lg shadow-lg">
            <div className="px-4 py-2 bg-gray-50 rounded-t-lg">
              <p className="text-xs font-medium text-gray-500 uppercase">
                {t('languages.selectLanguage')}
              </p>
            </div>

            <div className="py-1">
              {(Object.entries(supportedLanguages) as [SupportedLanguage, typeof supportedLanguages[SupportedLanguage]][]).map(
                ([langCode, language]) => (
                  <button
                    key={langCode}
                    onClick={() => handleLanguageSelect(langCode)}
                    className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-100 transition-colors ${
                      langCode === currentLanguage ? 'bg-blue-50' : ''
                    }`}
                    disabled={!language.available}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{language.flag}</span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {language.nativeName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {language.name}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {langCode === currentLanguage && (
                          <span className="flex items-center justify-center w-5 h-5 text-blue-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                        {!language.available && (
                          <span className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded">
                            {t('app.comingSoon')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Priority indicator for future languages */}
                    {!language.available && (
                      <div className="mt-1 text-xs text-gray-400">
                        {getPriority(langCode)}
                      </div>
                    )}
                  </button>
                )
              )}
            </div>

            {/* Footer Note */}
            <div className="px-4 py-2 bg-gray-50 rounded-b-lg">
              <p className="text-xs text-gray-500">
                Translation priorities: Malay → Chinese → Arabic
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
